import { Button, Divider, Spin, Tag } from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import ContentLayoutManagement from "../../../../layouts/ContentLayoutManagement";
import HeaderContent from "../../../../layouts/HeaderContent";
import CommonComponents from "../../../../components/Commons";
import useToggle from "../../../../hooks/useToggle";

import
{
   getOrderByIdUserAsync,
   updateStatusOrderAsync,
} from "../../../../store/reducers/orderSlice";
import { getStacItemDownloadJobUrl } from "../../../../services/download/downloadJobService";
import { RootState } from "../../../../store/store";
import { ESttausCode } from "../../../../enums";
import { getErrorMsg } from "../../../../helpers/common";
import { showError, showSuccess } from "../../../../helpers/toast";
import { setLoadingGlobal } from "../../../../store/reducers/appCommonSlice";
import
{
   addDownloads,
   selectHasActiveDownload,
} from "../../../../store/reducers/downloadSlice";
import { downloadManager } from "../../../../utils/downloadManager";

import CartImageOrder from "../../../../components/VMRS/Order/Tasking/CartImageOrder";
import { Section, Row } from "../../../../components/VMRS/Order/DetailSection";
import { ReasonCancelReject } from "../../../../components/VMRS/Order/Tasking/common";
import { useFetchDataRealtimeSync } from "../../../../components/VMRS/Order/hooks/useFetchDataRealtime";
import ImageInfo from "../../../../components/VMRS/ExploreArchive/ImageInfo";
import ImagesMapVMRS from "../../../../components/VMRS/MapAdapter/ImagesMap";
import { flyToGeometries, showFootprint } from "../../../../components/VMRS/ExploreArchive/ultis";
import
{
   changeBasemap,
   setBasemapList,
} from "../../../../components/VMRS/MapAdapter/store/slices/mapSlice";
import { clearAllLayers } from "../../../../components/VMRS/MapAdapter/store/slices/layerSlice";
import { clearAOIFeatures, setDrawingAOIMode } from "../../../../components/VMRS/MapAdapter/store/slices/aoiSlice";
import { setDrawingMode } from "../../../../components/VMRS/MapAdapter/store/slices/drawSlice";
import { setMeasureMode } from "../../../../components/VMRS/MapAdapter/store/slices/measureSlice";
import { BASEMAPS } from "../../../../components/VMRS/MapAdapter/utils/consts";
import { loadBasemaps } from "../../../../components/VMRS/MapAdapter/store/thunks/loadBasemaps";

// Đơn nháp (vừa tạo, chưa gửi): ORDERED = "Lưu nháp" (như đơn chụp mới); NEWORDER = tên của bản BE khác.
const DRAFT_STATUSES = ["ORDERED", "NEWORDER"];
// Statuses that show action buttons in the footer
const STATUSES_WITH_ACTIONS = [...DRAFT_STATUSES, "CONFIRMING_QUOTATION"];

export default function OrderArchiveDetail()
{
   const { t } = useTranslation();
   const { id } = useParams();
   const dispatch = useDispatch();
   const location = useLocation();
   const navigate = useNavigate();

   const [loading, setLoading] = useState(true);
   const [data, setData] = useState<any>({});
   const [reason, setReason] = useState<string>("");
   const [engineMap, setEngineMap] = useState<maplibregl.Map | undefined>(
      undefined,
   );
   const [selectedView, setSelectedView] = useState<any>(undefined);

   const notifications = useSelector(
      (state: RootState) => state.notificationSlice.total,
   );
   // A failed entry waits in the tray for a retry and must not lock the button.
   const hasActiveDownload = useSelector(selectHasActiveDownload);
   const dataStatusOrder = useSelector(
      (state: RootState) => state.appCommonSlice.dataStatusOrder,
   );

   const {
      mapType,
      view: mapView,
      selectedBasemap,
   } = useSelector((state: RootState) => state.map);

   const statusKey = data?.status?.key;

   const buyerName = (() =>
   {
      const b = data?.buyer?.name;
      if (!b) return undefined;
      return [b.first, b.last].filter(Boolean).join(" ");
   })();

   const getData = async (isActive: () => boolean = () => true) =>
   {
      try
      {
         setLoading(true);

         // @ts-ignore
         const res = await dispatch(getOrderByIdUserAsync(id)).unwrap();

         // Bail if the user navigated to another order while this request was in
         // flight — otherwise a slow response could draw a stale order's footprints.
         if (!isActive()) return;

         const order = res?.data?.data ?? {};
         setData(order);

         // Show all footprints on the map and fit bounds
         const geometries: any[] = [];
         (order?.items || []).forEach((it: any) =>
         {
            (it?.stac_items || []).forEach((s: any) =>
            {
               if (s?.geometry)
               {
                  showFootprint(
                     { ...s, provider_code: s?.provider_code ?? it?.provider_code },
                     dispatch,
                     engineMap,
                     true,
                  );
                  geometries.push(s.geometry);
               }
            });
         });

         if (geometries.length > 0)
         {
            flyToGeometries(geometries, engineMap);
         }
      } catch (error)
      {
      } finally
      {
         setLoading(false);
      }
   };

   useEffect(() =>
   {
      if (!engineMap || !id) return;

      let active = true;
      getData(() => active);
      return () =>
      {
         active = false;
      };
   }, [id, engineMap]);

   useEffect(() =>
   {
      // @ts-ignore – thunk
      dispatch(loadBasemaps());
   }, []);

   const handleResetMap = () =>
   {
      dispatch(clearAOIFeatures());
      dispatch(clearAllLayers());
      dispatch(setDrawingAOIMode("NONE"));
      dispatch(setDrawingMode("NONE"));
      dispatch(setMeasureMode("NONE"));
   };

   useEffect(() =>
   {
      // Reset AOI/layers/draw modes whenever the viewed order changes (the
      // component stays mounted across orders), so footprints shown for a
      // previous order don't linger on the map.
      handleResetMap();
      return () =>
      {
         handleResetMap();
      };
   }, [id]);

   useFetchDataRealtimeSync({
      notifications,
      getData,
      path: `order/management-order/${id}`,
   });

   // ================= STATUS ACTIONS =================
   const handleChangeStatusOrder = async (
      statusName: string,
      note: string,
      toggleCallback?: () => void,
   ) =>
   {
      try
      {
         setLoading(true);
         dispatch(setLoadingGlobal(true));
         toggleCallback?.();

         const statusId = dataStatusOrder?.find(
            (itemF: any) => itemF?.key === statusName,
         )?.id;
         // Danh sách trạng thái không có key này ⇒ đừng gọi `/status/undefined` (BE trả 500).
         if (statusId === undefined || statusId === null)
         {
            showError(t("Order status {{status}} is not available — please reload the page and try again.", { status: statusName }));
            return;
         }

         // @ts-ignore
         const result = await dispatch(
            // @ts-ignore
            updateStatusOrderAsync({
               order_id: id,
               status_id: statusId,
               notes: note,
            }),
         ).unwrap();

         if (result.status === ESttausCode.OK)
         {
            showSuccess(t("Successfully"));

            const next = dataStatusOrder?.find(
               (itemF: any) => itemF?.key === statusName,
            );
            setData((prev: any) => ({
               ...prev,
               status: {
                  ...prev.status,
                  key: next?.key,
                  id: next?.id,
                  name: next?.name,
                  color: next?.color,
               },
            }));
         }
      } catch (error)
      {
         showError(t(getErrorMsg(error)));
      } finally
      {
         dispatch(setLoadingGlobal(false));
         setLoading(false);
      }
   };

   const {
      open: openCancelOrder,
      toggle: toggleCancelOrder,
      shouldRender: shouldRenderCancelOrder,
   } = useToggle();

   const {
      open: openSendOrderUser,
      toggle: toggleSendOrderUser,
      shouldRender: shouldSendOrderUser,
   } = useToggle();

   const {
      open: openConfirmPrice,
      toggle: toggleConfirmPrice,
      shouldRender: shouldConfirmPrice,
   } = useToggle();

   const {
      open: openCancelPrice,
      toggle: toggleCancelPrice,
      shouldRender: shouldCancelPrice,
   } = useToggle();

   const {
      open: openViewInfo,
      shouldRender: shouldRenderViewInfo,
      toggle: toggleViewInfo,
   } = useToggle();

   // Gửi yêu cầu ⇒ IN_PROGRESS (Đang xử lý), như chi tiết đơn chụp mới và "Lưu và gửi yêu cầu" lúc tạo đơn.
   const handleSendOrderUser = () =>
   {
      handleChangeStatusOrder("IN_PROGRESS", "", toggleSendOrderUser);
   };

   const handleCancel = () =>
   {
      handleChangeStatusOrder("CANCELED", reason, toggleCancelOrder);
   };

   const handleConfirmPrice = () =>
   {
      handleChangeStatusOrder("CONFIRMED_QUOTATION", "", toggleConfirmPrice);
   };

   const handleCancelPrice = () =>
   {
      handleChangeStatusOrder("REJECTED_QUOTATION", "", toggleCancelPrice);
   };

   // Tải 1 ảnh (đơn READY_DOWNLOAD/CLOSED, ảnh `is_ready`): hỏi lại rồi chạy job nén như tải đơn chụp mới — cùng downloadManager
   // (chờ job READY rồi mở link có chữ ký), endpoint theo ảnh `/downloads/presigned/order-item/{orderItemId}?stac_item_id=`.
   const [downloadTarget, setDownloadTarget] = useState<{ orderItemId: string; image: any } | null>(null);
   const handleDownloadImage = () =>
   {
      const target = downloadTarget;
      setDownloadTarget(null);
      if (!target?.orderItemId || !target.image?.id)
      {
         showError(t("No files available for download"));
         return;
      }
      const file: any = {
         id: `file-${Date.now()}-${target.image.id}`,
         name: target.image.id,
         url: getStacItemDownloadJobUrl(target.orderItemId, target.image.id),
         progress: 0,
         status: "preparing" as const,
      };
      dispatch(addDownloads([file]));
      downloadManager.downloadFile(file);
   };

   const handleViewInfo = (item?: any) =>
   {
      setSelectedView(item);
      toggleViewInfo();
   };

   const showFooter = STATUSES_WITH_ACTIONS.includes(statusKey);

   // Only allow accept/cancel quotation when every provider has a price from admin
   const allProvidersPriced =
      (data?.items?.length ?? 0) > 0 &&
      (data?.items || []).every(
         (it: any) => it?.company_price !== null && it?.company_price !== undefined,
      );

   return (
      <ContentLayoutManagement
         header={
            <HeaderContent
               breadcrumb={[
                  {
                     title: t("Archive Orders"),
                     path: `/order/management-order${location.search}`,
                  },
                  {
                     title: data?.name,
                  },
               ]}
            />
         }
      >
         <div className="h-full flex bg-[var(--neutral-1000)] text-white">
            <div className="relative w-5/12 min-w-[380px] shrink-0 flex flex-col border-r border-[#26324a] pt-6">
               <Spin spinning={loading}>
                  <div
                     className="overflow-auto order-detail-scroll"
                     style={{ height: showFooter ? "calc(100% - 65px)" : "100%" }}
                  >
                     {/* Status / Order number / Created at */}
                     <div className="order-status-grid px-6 gap-2 mb-4">
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Status")}
                           </div>
                           <Tag
                              color={data?.status?.color}
                              className="px-3 py-1 rounded-full !text-white flex !justify-center"
                           >
                              {t(data?.status?.name)}
                           </Tag>
                        </div>
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Order number")}
                           </div>
                           <div className="text-sm">{data?.order_number || "--"}</div>
                        </div>
                        <div className="bg-[var(--neutral-950)] rounded-md p-3 text-left">
                           <div className="text-xs text-slate-400 mb-3">
                              {t("Ordered at")}
                           </div>
                           <div className="text-sm">
                              {data?.created_at
                                 ? dayjs(data.created_at).format("HH:mm DD/MM/YYYY")
                                 : "--"}
                           </div>
                        </div>
                     </div>

                     {/* General info */}
                     <Section title={t("General Information Request")}>
                        <Row label={t("Order name")} value={data?.name} />
                        <Row label={t("Reason")} value={data?.reason} />
                        <Row
                           label={t("Priority")}
                           value={t(data?.priority)}
                           highlight
                        />
                        <Row
                           label={t("Description")}
                           value={data?.description_order}
                        />
                        <Divider />
                        <Row label={t("Buyer")} value={buyerName} />
                        <Row label={t("Email")} value={data?.buyer?.email} />
                        {data?.notes && (
                           <>
                              <Divider />
                              <Row label={t("Notes")} warning value={data?.notes} />
                           </>
                        )}
                     </Section>

                     {/* Images grouped by provider */}
                     {(data?.items || []).map((it: any, idx: number) =>
                     {
                        const stacItems = it?.stac_items || [];
                        if (stacItems.length === 0) return null;

                        const hasPrice =
                           it?.company_price !== null &&
                           it?.company_price !== undefined;

                        const priceLabel = hasPrice
                           ? new Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: it?.currency_company || "USD",
                           }).format(Number(it.company_price))
                           : null;

                        return (
                           <Section
                              key={it?.id ?? idx}
                              title={it?.provider?.name || it?.provider_code}
                              extra={
                                 <div
                                    className="flex items-center gap-3"
                                    onClick={(e) => e.stopPropagation()}
                                 >
                                    <span className="text-xs text-slate-400 normal-case font-normal">
                                       ({stacItems.length} {t("images")})
                                    </span>
                                 </div>
                              }
                           >
                              {hasPrice && (
                                 <>
                                    <Row
                                       label={t("Price")}
                                       value={priceLabel}
                                       highlight
                                    />
                                    <Divider />
                                 </>
                              )}
                              {stacItems.map((image: any) => (
                                 <CartImageOrder
                                    key={image?.id}
                                    item={image}
                                    map={engineMap}
                                    providerCode={it?.provider_code}
                                    handleViewInfo={() => handleViewInfo(image)}
                                    onDownload={
                                       (statusKey === "READY_DOWNLOAD" || statusKey === "CLOSED") && image?.is_ready === true
                                          ? () => setDownloadTarget({ orderItemId: it?.id, image })
                                          : undefined
                                    }
                                    downloadDisabled={loading || hasActiveDownload}
                                 />
                              ))}
                           </Section>
                        );
                     })}
                  </div>

                  {/* ================= FOOTER ACTIONS ================= */}
                  {showFooter && (
                     <div
                        className="order-footer flex flex-wrap absolute bottom-0 w-full justify-between gap-2 px-6 py-4 bg-[var(--neutral-1000)]"
                        style={{ borderTop: "1px solid var(--divider-1)" }}
                     >
                        {/* Nháp ⇒ Hủy yêu cầu / Sửa / Gửi yêu cầu (như chi tiết đơn chụp mới). */}
                        {DRAFT_STATUSES.includes(statusKey) && (
                           <>
                              <span className="min-w-[110px] block">
                                 <Button
                                    onClick={() => toggleCancelOrder()}
                                    className="w-full !bg-[none] border border-[#FA5343] text-[#FA5343]"
                                    style={{ background: "none" }}
                                    disabled={loading}
                                 >
                                    {t("Cancel request")}
                                 </Button>
                              </span>
                              <div className="flex gap-2">
                                 <span className="block">
                                    <Button
                                       className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                                       style={{ background: "none" }}
                                       onClick={() => navigate(`./edit${location.search}`)}
                                       disabled={loading}
                                    >
                                       {t("Edit")}
                                    </Button>
                                 </span>
                                 <span className="min-w-[110px] block">
                                    <Button
                                       onClick={() => toggleSendOrderUser()}
                                       className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                       disabled={loading}
                                    >
                                       {t("Send request")}
                                    </Button>
                                 </span>
                              </div>
                           </>
                        )}

                        {statusKey === "CONFIRMING_QUOTATION" && allProvidersPriced && (
                           <div className="flex gap-2 justify-end flex-wrap w-full">
                              <span className="block">
                                 <Button
                                    className="w-full border border-[var(--primary-4)] !bg-[none] !text-[var(--primary-4)]"
                                    style={{ background: "none" }}
                                    onClick={() => toggleCancelPrice()}
                                    disabled={loading}
                                 >
                                    {t("Cancel quotation")}
                                 </Button>
                              </span>
                              <span className="min-w-[110px] block">
                                 <Button
                                    onClick={() => toggleConfirmPrice()}
                                    className="w-full !border-none !bg-[var(--primary-4)] text-[var(--primary-950)]"
                                    disabled={loading}
                                 >
                                    {t("Accept quotation")}
                                 </Button>
                              </span>
                           </div>
                        )}

                     </div>
                  )}
               </Spin>
            </div>

            <div className="flex-1 min-w-0 w-7/12">
               {selectedBasemap && (
                  <ImagesMapVMRS
                     mapType={mapType}
                     showBaseMap
                     showMapControl
                     view={mapView}
                     onSetEngineMap={(map: any) =>
                     {
                        if (!map) return;
                        map.loaded()
                           ? setEngineMap(map)
                           : map.once("load", () => setEngineMap(map));
                     }}
                  />
               )}
            </div>

            {shouldRenderCancelOrder && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openCancelOrder}
                  width={"450px"}
                  toggle={toggleCancelOrder}
                  handleConfirm={() =>
                  {
                     if (reason?.trim().length > 0)
                     {
                        handleCancel();
                     }
                  }}
                  disabled={reason?.trim().length === 0}
               >
                  <p className="mb-1">
                     {t("Are you sure you want to cancel this order?")}
                  </p>
                  <ReasonCancelReject reason={reason} setReasson={setReason} t={t} />
               </CommonComponents.ModalUI>
            )}

            {shouldSendOrderUser && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={openSendOrderUser}
                  toggle={toggleSendOrderUser}
                  handleConfirm={handleSendOrderUser}
               >
                  <p>
                     {t("Are you sure you want to send this order's request to admin?")}
                  </p>
               </CommonComponents.ModalUI>
            )}

            {shouldConfirmPrice && (
               <CommonComponents.ModalUI
                  title={t("Confirm quotation")}
                  open={openConfirmPrice}
                  toggle={toggleConfirmPrice}
                  handleConfirm={handleConfirmPrice}
               >
                  <p>{t("Are you sure you want to confirm price's this order?")}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldCancelPrice && (
               <CommonComponents.ModalUI
                  title={t("Cancel order")}
                  open={openCancelPrice}
                  toggle={toggleCancelPrice}
                  handleConfirm={handleCancelPrice}
               >
                  <p>{t("Are you sure you want to cancel this order?")}</p>
               </CommonComponents.ModalUI>
            )}

            {downloadTarget && (
               <CommonComponents.ModalUI
                  title={t("Confirm")}
                  open={!!downloadTarget}
                  toggle={() => setDownloadTarget(null)}
                  handleConfirm={handleDownloadImage}
               >
                  <p>{t("Do you want to download this image?")}</p>
                  <p className="m-0 mt-1 break-all font-mono text-xs text-slate-400">{downloadTarget.image?.id}</p>
               </CommonComponents.ModalUI>
            )}

            {shouldRenderViewInfo && (
               <CommonComponents.ModalUI
                  open={openViewInfo}
                  title={t("Information")}
                  toggle={() => toggleViewInfo()}
                  handleConfirm={() => { }}
                  hiddenFooter={true}
                  width={1120}
               >
                  <ImageInfo data={selectedView} open={openViewInfo} />
               </CommonComponents.ModalUI>
            )}
         </div>
      </ContentLayoutManagement>
   );
}
